/**
 * A gateway's update downloads (2026-10-19), on the gateway detail sheet's Settings tab: when it may fetch update
 * packages for its cache (PATCH /v1/things/{id} update_fetch), its next window on the plant's clock, what its update
 * store holds and its own last firmware update (from its birth). A person who may commission sets the mode and the
 * speed; anyone else reads them. The words are lib/iot/update-fetch's.
 */
import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { Label } from "@/lib/ui/label";
import { NativeSelect } from "@/lib/ui/native-select";
import type { Thing, UpdateFetchMode } from "@/lib/api/types";
import { SHIFT_ENDS_WARNING } from "@/lib/plant-time-form";
import {
  UPDATE_FETCH_MODES,
  firmwareWords,
  readFirmwareUpdate,
  readKbps,
  readUpdateStore,
  setWords,
  storeEntryWords,
  windowWords,
} from "@/lib/iot/update-fetch";

export interface GatewayUpdateFetchPanelProps {
  thing: Pick<Thing, "id" | "update_fetch" | "capabilities">;
  canEdit: boolean;
  now: Date;
  /** Saves the mode and speed (kbps null: the default); rejects with what went wrong. */
  onSave: (body: { mode: UpdateFetchMode; kbps: number | null }) => Promise<void>;
}

export function GatewayUpdateFetchPanel({ thing, canEdit, now, onSave }: GatewayUpdateFetchPanelProps) {
  const id = useId();
  const v = thing.update_fetch;
  const storedMode = (v?.mode ?? "off_shift") as UpdateFetchMode;
  const storedKbps = v && !v.default && v.kbps ? String(v.kbps) : "";
  const [mode, setMode] = useState<UpdateFetchMode>(storedMode);
  const [kbpsText, setKbpsText] = useState(storedKbps);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A saved policy (or one the gateway reported, or another gateway) replaces the draft, while rendering.
  const storedKey = `${thing.id}|${storedMode}|${storedKbps}`;
  const [draftOf, setDraftOf] = useState(storedKey);
  if (draftOf !== storedKey) {
    setDraftOf(storedKey);
    setMode(storedMode);
    setKbpsText(storedKbps);
  }

  if (!v) return null;
  const store = readUpdateStore(thing.capabilities);
  const firmware = readFirmwareUpdate(thing.capabilities);
  const kbps = readKbps(kbpsText);
  const changed = mode !== storedMode || kbpsText.trim() !== storedKbps;
  const hint = UPDATE_FETCH_MODES.find((m) => m.value === mode)?.hint;
  const byShifts = v.mode === "off_shift" || v.mode === "during_shift";

  const save = async () => {
    if (saving || kbps.problem) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({ mode, kbps: kbps.kbps });
    } catch (err) {
      setError(err instanceof Error ? err.message : "The setting could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section aria-label="Update downloads" className="flex flex-col gap-3 rounded-md border border-border p-3">
      <header className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">Update downloads</header>
      <p className="text-xs text-on-surface-muted">
        The gateway downloads updates for the PCs behind it, and its own firmware, only when this allows. The PCs then
        take them from the gateway.
      </p>
      <p className="text-sm text-on-surface">{windowWords(v, now)}</p>
      {byShifts && !v.shift_ends && (
        <p role="note" className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-on-surface">
          {SHIFT_ENDS_WARNING}{" "}
          <Link className="underline" to="/app/settings?tab=plant-time">
            Plant time
          </Link>
        </p>
      )}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${id}-mode`}>When</Label>
          <NativeSelect
            id={`${id}-mode`}
            className="w-56"
            disabled={!canEdit || saving}
            value={mode}
            onChange={(e) => {
              setError(null);
              setMode(e.target.value as UpdateFetchMode);
            }}
          >
            {UPDATE_FETCH_MODES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${id}-kbps`}>Speed (kbit/s)</Label>
          <Input
            id={`${id}-kbps`}
            inputMode="numeric"
            className="w-36"
            disabled={!canEdit || saving}
            placeholder={`${v.kbps} (default)`}
            value={kbpsText}
            onChange={(e) => {
              setError(null);
              setKbpsText(e.target.value);
            }}
          />
        </div>
        {canEdit && (
          <Button type="button" size="sm" onClick={() => void save()} disabled={!changed || !!kbps.problem || saving}>
            {saving ? <Loader2 className="animate-spin" aria-hidden /> : <Save aria-hidden />}
            Save
          </Button>
        )}
      </div>
      {hint && <p className="text-xs text-on-surface-muted">{hint}</p>}
      {kbps.problem && (
        <p role="alert" className="text-xs text-danger">
          {kbps.problem}
        </p>
      )}
      {error && (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
      <p className="text-xs text-on-surface-muted">{setWords(v, now)}</p>

      <div className="flex flex-col gap-1">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">On the gateway</span>
        {store.length === 0 && !firmware ? (
          <p className="text-xs text-on-surface-muted">It reports no downloaded updates.</p>
        ) : (
          <ul className="flex flex-col gap-0.5 text-xs text-on-surface">
            {store.map((e) => (
              <li key={`${e.product}-${e.target}-${e.version}`}>{storeEntryWords(e)}</li>
            ))}
            {firmware && <li>{firmwareWords(firmware)}</li>}
          </ul>
        )}
      </div>
    </section>
  );
}
